import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { AuthService } from '../shared/auth/auth.service';
import { ErrorService } from '../shared/error.service';

@Component({
  selector: 'app-service-request-wrapper',
  standalone: true,
  imports: [CustomTabsComponent, RouterOutlet],
  templateUrl: './service-request-wrapper.component.html',
  styleUrl: './service-request-wrapper.component.scss'
})
export class ServiceRequestWrapperComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private errorService = inject(ErrorService);

  // ✅ Define tabs matching EXACTLY the 5 core service request categories
  private allTabs: TabItem[] = [
    { 
      id: 'lab-tests', 
      label: 'Laboratory', 
      categoryCode: 'laboratory'  // Matches backend code
    },
    { 
      id: 'imaging', 
      label: 'Imaging', 
      categoryCode: 'imaging'
    },
    { 
      id: 'procedure', 
      label: 'Procedure', 
      categoryCode: 'procedure'
    },
    { 
      id: 'surgery', 
      label: 'Surgery', 
      categoryCode: 'surgery'
    },
    {
      id: 'observation',
      label: 'Observation',
      categoryCode: 'observation'
    },
    { 
      id: 'others', 
      label: 'Others', 
      categoryCode: 'others'
    }
  ];

  tabs: TabItem[] = [];
  activeTabId = 'lab-tests';
  private sub?: Subscription;

  ngOnInit(): void {
    this.filterTabsByPermissions();
    this.syncActiveTabFromUrl();
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.syncActiveTabFromUrl());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private filterTabsByPermissions(): void {
    const currentUser = this.authService.user.getValue();
    
    if (!currentUser) {
      this.tabs = [];
      return;
    }

    // ✅ SuperAdmin and Admin see all tabs
    if (currentUser.isSuperAdmin || currentUser.role?.toLowerCase() === 'admin') {
      this.tabs = [...this.allTabs];
      console.log('👑 Admin/SuperAdmin - showing all tabs');
      return;
    }

    const userRole = (currentUser.role || '').toLowerCase();
    
    // ✅ HCA and Nurse/Midwife see all tabs EXCEPT laboratory and imaging
    if (userRole === 'healthcareassistant' || userRole === 'nursemidwife') {
      this.tabs = this.allTabs.filter(tab => {
        const cat = (tab.categoryCode ?? '').toLowerCase().trim();
        return cat !== 'laboratory' && cat !== 'imaging' && cat !== 'lab';
      });
      console.log('🩺 HCA/Nurse - showing all tabs except Lab & Imaging');
      return;
    }

    // ✅ Get allowed service request categories from user permissions
    const allowedCategories = this.authService.getAllowedServiceRequestCategories();
    
    console.log('🔍 User allowed service request categories:', allowedCategories);

    // ✅ Check if user has full access
    if (this.hasFullAccess(currentUser)) {
      this.tabs = [...this.allTabs];
      console.log('✨ User has full access - showing all tabs');
      return;
    }

    // ✅ If no categories defined, no tabs shown
    if (!allowedCategories || allowedCategories.length === 0) {
      console.warn('⚠️ User has no service request categories defined');
      this.tabs = [];
      return;
    }

    // ✅ Convert allowed categories to lowercase for case-insensitive matching
    const allowedCategoriesLower = allowedCategories.map((cat: string) => 
      cat.toLowerCase().trim()
    );

    // ✅ Filter tabs based on EXACT category code matching (case-insensitive)
    this.tabs = this.allTabs.filter(tab => {
      const isAllowed = allowedCategoriesLower.includes(
        (tab.categoryCode ?? '').toLowerCase().trim()
      );
      
      console.log(
        `📋 Tab "${tab.label}" (${tab.categoryCode}): ${isAllowed ? '✅ ALLOWED' : '❌ BLOCKED'}`
      );
      
      return isAllowed;
    });

    console.log('✅ Final visible tabs:', this.tabs.map(t => t.label));

    if (this.tabs.length === 0) {
      console.warn('⚠️ User has no visible service request tabs based on permissions');
    }
  }

  /**
   * Check if user has full access to all service requests
   */
  private hasFullAccess(user: any): boolean {
    try {
      if (!user.rolePermissionsJson) {
        return false;
      }

      const permissions = JSON.parse(user.rolePermissionsJson);
      
      if (Array.isArray(permissions)) {
        const currentRoleConfig = permissions.find(
          (p: any) => p.RoleName?.toLowerCase() === user.role?.toLowerCase()
        );

        return currentRoleConfig?.HasFullAccess === true;
      }

      return false;
    } catch (error) {
      console.error('Error checking full access:', error);
      return false;
    }
  }

  onTabClicked(tabId: string): void {
    this.activeTabId = tabId;
    this.router.navigate([tabId], { relativeTo: this.route });
  }

  private syncActiveTabFromUrl(): void {
    const child = this.route.firstChild;
    const segment = child?.snapshot?.url?.[0]?.path;
    
    if (!segment) {
      // Navigate to first available tab if current user has permissions
      if (this.tabs.length > 0) {
        this.activeTabId = this.tabs[0].id;
        this.router.navigate([this.tabs[0].id], { relativeTo: this.route, replaceUrl: true });
      } else {
        // ✅ No tabs available
        console.error('❌ User has no access to any service request tabs');
        this.errorService.openandCloseError('You do not have permission to view any service requests.');
        this.router.navigate(['../summary'], { relativeTo: this.route, replaceUrl: true });
      }
      return;
    }
    
    // ✅ Only set active tab if user has permission for it
    const hasPermission = this.tabs.some(tab => tab.id === segment);
    if (hasPermission) {
      this.activeTabId = segment;
    } else if (this.tabs.length > 0) {
      // ✅ Redirect to first allowed tab if trying to access unauthorized tab
      console.warn(`⚠️ User tried to access unauthorized tab: ${segment}, redirecting to ${this.tabs[0].id}`);
      this.activeTabId = this.tabs[0].id;
      this.router.navigate([this.tabs[0].id], { relativeTo: this.route, replaceUrl: true });
    } else {
      // ✅ No tabs available at all
      console.error('❌ User has no access to any tabs');
      this.errorService.openandCloseError('You do not have permission to view any service requests.');
      this.router.navigate(['../summary'], { relativeTo: this.route, replaceUrl: true });
    }
  }
}
